import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface SimpleThreeProps {
  modelUrl?: string;
}

// Mobile-optimized performance settings
const getPerformanceSettings = () => {
  const isMobile = window.innerWidth <= 768;
  const isLowEnd = navigator.hardwareConcurrency <= 4;
  
  if (isMobile) {
    return {
      antialias: false, // Disable antialiasing on mobile for performance
      pixelRatio: Math.min(window.devicePixelRatio, 2), // Cap pixel ratio to 2x on mobile
      shadowMapSize: 512, // Lower shadow quality
      modelScale: 3.2, // Reduced from 4.4 for better mobile visibility
      floatingIntensity: 0.04, // Reduce floating animation intensity
      enableFloating: true,
      targetFPS: 30, // Lower target FPS for mobile
      smoothingFactor: 0.03, // Much slower smoothing for mobile
      touchSmoothingFactor: 0.015, // Even slower when touching
      maxScaleAddition: 2.5, // Reduced scaling range for mobile (was 3)
      touchThrottleMs: 32 // Throttle updates during touch to ~30fps
    };
  }
  
  if (isLowEnd) {
    return {
      antialias: true,
      pixelRatio: Math.min(window.devicePixelRatio, 2.5),
      shadowMapSize: 768,
      modelScale: 4.4,
      floatingIntensity: 0.06,
      enableFloating: true,
      targetFPS: 45,
      smoothingFactor: 0.08,
      touchSmoothingFactor: 0.05,
      maxScaleAddition: 4,
      touchThrottleMs: 22
    };
  }
  
  return {
    antialias: true,
    pixelRatio: window.devicePixelRatio,
    shadowMapSize: 1024,
    modelScale: 4.4,
    floatingIntensity: 0.08,
    enableFloating: true,
    targetFPS: 60,
    smoothingFactor: 0.1,
    touchSmoothingFactor: 0.08,
    maxScaleAddition: 6,
    touchThrottleMs: 16
  };
};

export const SimpleThree: React.FC<SimpleThreeProps> = ({ modelUrl }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const frameRef = useRef<number | null>(null);
  const modelRef = useRef<THREE.Object3D | null>(null);
  const cameraRef = useRef<THREE.Camera | null>(null);
  const performanceRef = useRef(getPerformanceSettings());
  const lastFrameTime = useRef(Date.now());
  const isVisible = useRef(true);
  const needsRender = useRef(true);
  const [currentWord, setCurrentWord] = React.useState("BESTE");
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  
  // Touch and mobile optimization refs
  const isTouchingRef = useRef(false);
  const touchStartTimeRef = useRef(0);
  const lastTouchMoveRef = useRef(0);
  const smoothUpdateRef = useRef<number | null>(null);
  const smoothDataRef = useRef<{ current: number; target: number } | null>(null);

  // Optimized text transition function
  const changeWordWithTransition = useCallback((newWord: string) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Faster, simpler transition
    gsap.to(".adjective-text", {
      opacity: 0,
      duration: 0.08,
      ease: "power1.out",
      onComplete: () => {
        setCurrentWord(newWord);
        gsap.to(".adjective-text", {
          opacity: 1,
          duration: 0.08,
          ease: "power1.out",
          onComplete: () => setIsTransitioning(false)
        });
      }
    });
  }, [isTransitioning]);

  useEffect(() => {
    if (!mountRef.current) return;

    // Setup with performance optimizations
    const performance = performanceRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    
    console.log('[SimpleThree] Creating WebGL renderer...');
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: performance.antialias,
      powerPreference: "high-performance"
    });
    
    // Add context loss handling
    const canvas = renderer.domElement;
    canvas.addEventListener('webglcontextlost', (event) => {
      console.error('[SimpleThree] WebGL context lost!', event);
      event.preventDefault();
    });
    
    canvas.addEventListener('webglcontextrestored', (event) => {
      console.log('[SimpleThree] WebGL context restored!', event);
    });
    
    renderer.setPixelRatio(performance.pixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    
    console.log('[SimpleThree] Renderer created successfully, adding to DOM');
    mountRef.current.appendChild(renderer.domElement);

    // Parallax scroll variables
    let scrollY = 0;
    let baseY = -1; // Base Y position

    // Load GLTF model or fallback to cube
    const defaultModelUrl = 'https://aogzcyknlimbgcwjukah.supabase.co/storage/v1/object/public/3d-models/protien_jar.glb';
    const finalModelUrl = (modelUrl && modelUrl.trim() !== '') ? modelUrl : defaultModelUrl;
    console.log('[SimpleThree] Using model URL:', finalModelUrl);

    const loader = new GLTFLoader();
    
    // Load the model
    loader.load(
      finalModelUrl,
      (gltf) => {
        console.log('GLTF model loaded successfully:', gltf);
        const model = gltf.scene;
        
        // Log model structure to understand how to animate the lid
        console.log('Model children:', model.children);
        model.traverse((child) => {
          console.log('Child found:', child.name, child.type);
        });
        
        // Scale and position the model with performance-based scaling
        model.scale.setScalar(performance.modelScale);
        model.position.set(0, -1, 0);
        
        scene.add(model);
        modelRef.current = model;
        
        // Force first render on model load
        needsRender.current = true;
        renderer.render(scene, camera);
        
        // Setup special animation for "Hvorfor velge PEVEO" section
        setupWhyChooseAnimation(model, camera, scene);
        
        console.log('Model added to scene');
      },
      (progress) => {
        console.log('Loading progress:', (progress.loaded / progress.total * 100) + '%');
      },
      (error) => {
        console.error('Error loading GLTF model:', error);
        
        // Fallback to cube
        const geometry = new THREE.BoxGeometry(2, 2, 2);
        const material = new THREE.MeshPhongMaterial({ 
          color: 0xffd700,
          transparent: true,
          opacity: 0.8
        });
        const cube = new THREE.Mesh(geometry, material);
        cube.position.set(0, -1, 0);
        scene.add(cube);
        modelRef.current = cube;
        
        // Force first render on fallback
        needsRender.current = true;
        renderer.render(scene, camera);
        
        // Setup special animation for fallback cube too
        setupWhyChooseAnimation(cube, camera, scene);
        
        console.log('Fallback cube added');
      }
    );

    // Optimized scroll-based animation with smooth interpolation
    const setupWhyChooseAnimation = (model: THREE.Object3D, cam: THREE.Camera, _scene: THREE.Scene) => {
      // Shared mutable state for smooth interpolation (merged into main animate loop)
      const smooth = { current: 0, target: 0 };
      smoothDataRef.current = smooth;
      
      gsap.timeline({
        scrollTrigger: {
          trigger: "body",
          start: "top top",
          end: "bottom bottom",
          scrub: 1.2,
          onUpdate: (self) => {
            smooth.target = self.progress;
            needsRender.current = true;
          }
        }
      });

      // Create individual ScrollTriggers for each section to change words
      const sections = [
        { id: "#why-choose-peveo", word: "BESTE" },      // Forskningsbasert section
        { id: "#optimal-dosering", word: "STERKESTE" },  // Optimal dosering
        { id: "#good-cause", word: "BILLIGSTE" },        // Best på pris  
        { id: "#billigste", word: "SNILLESTE" },         // For en god sak
        { id: "#safest", word: "TRYGGESTE" },            // Doping tested section
        { id: "#contact", word: "PRØV PEVEO", end: "top 80%" }   // Contact section - fade out earlier
      ];

      // Set initial state - text hidden by default
      gsap.set(".static-text-container", { opacity: 0 });

      // Create ScrollTriggers for each section - show text and change words
      sections.forEach((section, index) => {
        ScrollTrigger.create({
          trigger: section.id,
          start: "top 60%",     
          end: section.end || "bottom 40%",    // Use custom end if provided
          onEnter: () => {
            console.log(`Entering section: ${section.id} - ${section.word}`);
            gsap.to(".static-text-container", {
              opacity: 1,
              duration: 0.2,
              ease: "power2.out"
            });
            
            // Hide "NORGES" for contact section, show for others
            if (section.id === "#contact") {
              gsap.to(".intro-word:first-child", { opacity: 0, duration: 0.2 });
            } else {
              gsap.to(".intro-word:first-child", { opacity: 1, duration: 0.2 });
            }
            
            changeWordWithTransition(section.word);
          },
          onLeave: () => {
            // For contact section, hide text earlier when leaving
            if (section.id === "#contact") {
              console.log(`Leaving contact section early: ${section.id}`);
              gsap.to(".static-text-container", {
                opacity: 0,
                duration: 0.3,
                ease: "power2.out"
              });
            } else if (index === sections.length - 1) { // Only hide after last section for others
              console.log(`Leaving last section: ${section.id}`);
              gsap.to(".static-text-container", {
                opacity: 0,
                duration: 0.2,
                ease: "power2.out"
              });
            }
          },
          onLeaveBack: () => {
            // Hide text when scrolling back before first section
            if (index === 0) { // Only hide before first section
              console.log(`Leaving back first section: ${section.id}`);
              gsap.to(".static-text-container", {
                opacity: 0,
                duration: 0.2,
                ease: "power2.out"
              });
            }
          }
        });
      });
    };

    // Lighting - 5% less shadows and better illumination
    const ambientLight = new THREE.AmbientLight(0x404040, 0.7); // Increased from 0.6
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8); // Reduced from 1.0
    directionalLight.position.set(5, 8, 3); // Softer position
    scene.add(directionalLight);
    
    // Add fill light to reduce harsh shadows
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-5, -5, -3);
    scene.add(fillLight);

    camera.position.z = 12;

    // Touch event handlers for mobile optimization
    const handleTouchStart = () => {
      isTouchingRef.current = true;
      touchStartTimeRef.current = Date.now();
      lastTouchMoveRef.current = Date.now();
    };

    const handleTouchMove = () => {
      if (isTouchingRef.current) {
        const now = Date.now();
        // Throttle touch move events for better performance
        if (now - lastTouchMoveRef.current >= performance.touchThrottleMs) {
          lastTouchMoveRef.current = now;
          needsRender.current = true;
        }
      }
    };

    const handleTouchEnd = () => {
      isTouchingRef.current = false;
      touchStartTimeRef.current = 0;
    };

    // Add touch event listeners for mobile optimization
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    // Scroll handler for parallax effect
    const handleScroll = () => {
      scrollY = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Store refs
    sceneRef.current = scene;
    rendererRef.current = renderer;
    cameraRef.current = camera;

    // Visibility detection for performance
    const updateVisibility = () => {
      isVisible.current = !document.hidden && 
        window.scrollY >= 0 && 
        window.scrollY <= document.body.scrollHeight;
    };
    
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('scroll', updateVisibility, { passive: true });

    // High-performance animation loop for all devices
    let lastFrameTime = 0;
    const frameInterval = 1000 / performance.targetFPS;
    
    const animate = (currentTime: number) => {
      frameRef.current = requestAnimationFrame(animate);
      
      if (!isVisible.current || (currentTime - lastFrameTime < frameInterval && !needsRender.current)) {
        return;
      }
      
      lastFrameTime = currentTime;
      needsRender.current = false;
      
      // Merged smooth interpolation (previously a separate rAF loop)
      const smooth = smoothDataRef.current;
      if (smooth && Math.abs(smooth.target - smooth.current) > 0.001) {
        const activeSmoothingFactor = isTouchingRef.current 
          ? performance.touchSmoothingFactor 
          : performance.smoothingFactor;
        smooth.current += (smooth.target - smooth.current) * activeSmoothingFactor;
        
        if (modelRef.current) {
          const baseScale = performance.modelScale;
          const scaleProgress = Math.pow(smooth.current, 1.2);
          modelRef.current.scale.setScalar(baseScale + performance.maxScaleAddition * scaleProgress);
          modelRef.current.rotation.set(Math.PI * 0.5 * smooth.current, Math.PI * 4 * smooth.current, 0);
          camera.position.set(0, 1 * smooth.current, 12 - (2 * smooth.current));
        }
        needsRender.current = true;
      }
      
      // Apply floating animation
      if (modelRef.current && isVisible.current && performance.enableFloating) {
        const floatingAnimation = Math.sin(currentTime * 0.0008) * performance.floatingIntensity;
        modelRef.current.position.y = -1 + floatingAnimation;
      }
      
      renderer.render(scene, camera);
    };
    requestAnimationFrame(animate);

    // Throttled resize handler
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);

        // Recompute performance settings on resize and update DPR
        const newPerf = getPerformanceSettings();
        performanceRef.current = newPerf;
        renderer.setPixelRatio(newPerf.pixelRatio);

        needsRender.current = true;
      }, 100);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      document.removeEventListener('visibilitychange', updateVisibility);
      
      // Kill all ScrollTrigger instances for this component
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      if (smoothUpdateRef.current) {
        cancelAnimationFrame(smoothUpdateRef.current);
      }
      
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      
      // Enhanced cleanup
      renderer.dispose();
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry?.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material?.dispose();
          }
        }
      });
    };
  }, [modelUrl]); // Add modelUrl as dependency

  return (
    <>
      <div 
        ref={mountRef} 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{ width: '100%', height: '100%' }}
      />
      {/* Fixed text element - starts hidden and shows in sections */}
      <div className="fixed inset-0 flex items-center justify-center z-0 pointer-events-none static-text-container" style={{ opacity: 0 }}>
        <div className="relative text-center px-4">
          <div className="fixed inset-0 bg-gradient-to-b from-transparent via-background/40 to-background/90 -z-10"></div>
           <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-[9rem] font-bold select-none font-orbit leading-tight intro-word" style={{ 
             color: 'rgba(255, 215, 0, 0.4)', // More transparent gold
             textShadow: '2px 2px 4px rgba(0, 0, 0, 0.6), 1px 1px 2px rgba(0, 0, 0, 0.5), 0 0 8px rgba(255, 215, 0, 0.2)', // More subtle shadows
             filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))', // Lighter drop shadow
             WebkitTextStroke: '0.5px rgba(255, 215, 0, 0.15)' // More transparent stroke
           }}>
             NORGES
           </h2>
           <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-[9rem] font-bold select-none font-orbit leading-tight adjective-text intro-word" style={{ 
             color: 'rgba(255, 215, 0, 0.5)', // More transparent gold for the adjective
             textShadow: '2px 2px 4px rgba(0, 0, 0, 0.6), 1px 1px 2px rgba(0, 0, 0, 0.5), 0 0 8px rgba(255, 215, 0, 0.25)', // More subtle shadows
             filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))', // Lighter drop shadow
             WebkitTextStroke: '0.5px rgba(255, 215, 0, 0.2)' // More transparent stroke
           }}>
             {currentWord}
           </h2>
        </div>
      </div>
    </>
  );
};